/* eslint-disable react-hooks/exhaustive-deps */
import MonthPicker from "./MonthPicker";
import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { getPageRouteFromKey } from "./routesHelper";
import { pushLevel, setAdminOption } from "../../../../middlewares";
import { useStaticContext } from "../../../../context/static/StaticContext";
import { addOneLevel } from "../../../../middlewares/breadCrumb/BreadCrumb";
import { useAdminContext } from "../../../../context/admin/context/AdminContext";
import { getType } from "./helper";
import {
  RiBarChart2Line,
  RiUserLine,
  RiMailLine,
  RiCalendar2Line,
  RiArrowRightUpLine,
} from "react-icons/ri";

const AdminComponent = () => {
  const { getStats, stats } = useStaticContext();
  const { sysAdmin, setSingleAdmin } = useAdminContext();

  const fullName =
    `${sysAdmin?.first_name || ""} ${sysAdmin?.last_name || ""}`.trim() || "Admin";
  const email = sysAdmin?.email || "";
  const admin_id = sysAdmin?.admin_id;

  const [localStatistics, setLocalSetStats] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!stats) {
      loadStatistics({ year: currentYear, month: currentMonth });
    } else {
      setLocalSetStats(buildArrayOfKeysAndValuesFromAdminObjectData(stats));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats]);

  const buildArrayOfKeysAndValuesFromAdminObjectData = (obj) => {
    const result = [];
    let total_items = 0;
    for (const key in obj) {
      if (!["email", "admin_id", "last_name", "first_name", "phonenumber"].includes(String(key))) {
        total_items += obj[key];
        result.push({
          title: key,
          value: obj[key],
          route: getPageRouteFromKey({ key }),
        });
      }
    }
    setTotal(total_items);
    return result;
  };

  const loadStatistics = async ({ year, month }) => {
    await getStats({ year, month, data: { ids: [sysAdmin?.admin_id] } });
  };

  // tones for stat tiles (visual only)
  const tones = [
    { grad: "from-emerald-500 via-teal-500 to-cyan-500", soft: "bg-emerald-50", text: "text-emerald-700" },
    { grad: "from-fuchsia-500 via-pink-500 to-rose-500", soft: "bg-pink-50", text: "text-pink-700" },
    { grad: "from-indigo-500 via-violet-500 to-purple-500", soft: "bg-violet-50", text: "text-indigo-700" },
    { grad: "from-amber-500 via-orange-500 to-red-500", soft: "bg-amber-50", text: "text-amber-700" },
    { grad: "from-sky-500 via-blue-500 to-indigo-500", soft: "bg-sky-50", text: "text-sky-700" },
    { grad: "from-lime-500 via-green-500 to-emerald-500", soft: "bg-lime-50", text: "text-lime-700" },
  ];

  return (
    <div className="flex flex-col w-full xl:w-10/12 mx-auto gap-6">
      {/* Toolbar / Filters */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4">
        {/* STACKED header so name/email are on TOP */}
        <div className="flex flex-col gap-4">
          {/* Identity block (top) */}
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-gray-900 text-white flex items-center justify-center">
              <RiUserLine />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{fullName}</p>
              {email ? (
                <p className="text-[12px] text-gray-600 flex items-center gap-1 truncate">
                  <RiMailLine /> {email}
                </p>
              ) : null}
              <p className="mt-1 text-[11px] text-gray-500 flex items-center gap-1">
                <RiCalendar2Line /> {String(currentMonth).padStart(2, "0")}/{currentYear}
              </p>
            </div>
          </div>

          {/* Controls (bottom of header; sits under identity on small screens) */}
          <div className="text-sm text-gray-900">
            <MonthPicker
              currentYear={currentYear}
              currentMonth={currentMonth}
              setTotal={setTotal}
              setCurrentYear={setCurrentYear}
              loadStatistics={loadStatistics}
              setCurrentMonth={setCurrentMonth}
              setLocalSetStats={setLocalSetStats}
            />
          </div>
        </div>
      </div>

      {/* Header strip */}
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] text-gray-700">
          <RiBarChart2Line />
          My Statistics
        </div>
        <div className="text-xs md:text-sm text-gray-600">
          Total Item(s):{" "}
          <span className="font-semibold text-gray-900">[ {total} Item(s) ]</span>
        </div>
      </div>

      {/* Stats grid */}
      {localStatistics ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {localStatistics.map((stat, idx) => {
            const tone = tones[idx % tones.length];
            const title = stat?.title?.replace(/_/g, " ");
            return (
              <Link
                key={`${title}-${idx}`}
                to={stat.route}
                onClick={() => {
                  setSingleAdmin({
                    ids: [admin_id],
                    year: currentYear,
                    month: currentMonth,
                    type: getType({ field: stat?.title }),
                    route: "/content/stats",
                  });
                  // Breadcrumb
                  addOneLevel({
                    id: 0,
                    name: fullName || "Admin",
                    route: "/levels/first",
                    level: "",
                    country_id: 0,
                    data: { data: { name: "", country_id: 0, first_level_id: 0, second_level_id: 0, third_level_id: 0, fourth_level_id: 0, level: "" } },
                  });
                  pushLevel({ level: { id: 0, name: "", level: "All", route: "/levels/first", data: null } });
                  setAdminOption(sysAdmin?.admin_id);
                }}
                className="group"
              >
                {/* Gradient frame (CLIPPED to prevent border overflow) */}
                <div
                  className={`relative rounded-2xl p-[1px] bg-gradient-to-br ${tone.grad} shadow-sm overflow-hidden transition-transform duration-200 group-hover:-translate-y-0.5`}
                >
                  {/* Card */}
                  <div className="relative rounded-[14px] bg-white shadow-sm ring-1 ring-black/5 p-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">Category</p>
                      <p className="text-sm font-semibold text-gray-900 truncate capitalize">{title}</p>
                      <p className={`mt-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium ${tone.soft} ${tone.text} ring-1 ring-black/5`}>
                        Manage • Review • Publish
                      </p>
                    </div>

                    <div className="shrink-0 flex flex-col items-end gap-2">
                      <span className="inline-flex items-center justify-center rounded-xl bg-gray-900 text-white px-3 py-2 text-sm font-semibold shadow-sm">
                        {stat?.value}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-gray-600 group-hover:text-gray-900">
                        Open <RiArrowRightUpLine />
                      </span>
                    </div>

                    {/* Accent bar INSIDE the card to avoid overflow */}
                    <div className={`pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r ${tone.grad} rounded-b-[14px]`} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        // Skeletons
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 animate-pulse">
              <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
              <div className="h-4 w-40 bg-gray-200 rounded mb-4" />
              <div className="h-6 w-14 bg-gray-200 rounded ml-auto" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminComponent;
